import { useEffect, useRef } from 'react';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  twitterCard?: string;
  schemaData?: Record<string, unknown> | Record<string, unknown>[];
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = 'website',
  twitterCard = 'summary_large_image',
  schemaData,
}: SEOHeadProps) {
  const schemaRef = useRef<HTMLScriptElement | null>(null);

  useEffect(() => {
    const baseTitle = '动漫之家 - 韩国漫画无删减免费阅读';
    const fullTitle = title ? `${title} - ${baseTitle}` : baseTitle;
    document.title = fullTitle;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    if (description) {
      setMeta('description', description);
      setMeta('og:description', description, true);
    }
    if (keywords) setMeta('keywords', keywords);
    if (canonicalUrl) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonicalUrl);
    }
    if (ogTitle || title) {
      setMeta('og:title', ogTitle || fullTitle, true);
    }
    if (ogImage) setMeta('og:image', ogImage, true);
    setMeta('og:type', ogType, true);
    if (canonicalUrl) setMeta('og:url', canonicalUrl, true);
    setMeta('twitter:card', twitterCard);
    if (ogTitle || title) setMeta('twitter:title', ogTitle || fullTitle);
    if (description) setMeta('twitter:description', description);

    return () => {
      if (schemaRef.current) {
        schemaRef.current.remove();
        schemaRef.current = null;
      }
    };
  }, [title, description, keywords, canonicalUrl, ogTitle, ogDescription, ogImage, ogType, twitterCard]);

  useEffect(() => {
    const existingScript = document.querySelector('script[data-schema="dynamic"]');
    if (existingScript) existingScript.remove();

    if (schemaData && typeof window !== 'undefined') {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-schema', 'dynamic');
      const dataArray = Array.isArray(schemaData) ? schemaData : [schemaData];
      script.textContent = JSON.stringify(dataArray.length === 1 ? dataArray[0] : dataArray, null, 2);
      document.head.appendChild(script);
      schemaRef.current = script;
    }
  }, [schemaData]);

  return null;
}